/**
 * Protected route — redirects to login if not authenticated.
 * Shows loading spinner during auth check.
 *
 * R32a fix: Auth state is loaded asynchronously from localStorage on mount.
 * Without the isLoading guard, ProtectedRoute evaluates isAuthenticated=false
 * before initializeAuth() completes and immediately redirects to /login.
 * The login page then detects the restored session and redirects back to /dashboard,
 * creating an infinite redirect loop. Fix: render a spinner while isLoading=true
 * so the route guard never fires until auth state is fully initialized.
 *
 * NP-220 fix: Simplified authentication check to prevent race conditions
 * and confusion between AuthContext state and separate token checks.
 *
 * BUG-DEEP-010 fix: For unauthenticated users hitting truly unknown paths,
 * render the 404 NotFound page directly instead of redirecting to login
 * (Sven confirmed: /totally-nonexistent-page-xyz123 → login, not 404 page).
 */

import { Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * P0 WHITE-PAGE FIX: Shows a visible timeout message when loading takes too long.
 * Prevents users from seeing a blank/frozen spinner for >8 seconds without feedback.
 */
function LoadingWithTimeout() {
  const [showTimeout, setShowTimeout] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setShowTimeout(true), 8_000);
    return () => clearTimeout(id);
  }, []);

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4" data-testid="auth-loading">
      {!showTimeout ? (
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      ) : (
        <>
          <p className="text-sm text-muted-foreground max-w-sm text-center">
            Loading is taking longer than expected.
          </p>
          <button
            className="rounded bg-primary px-4 py-2 text-sm text-primary-foreground hover:opacity-90"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </>
      )}
    </div>
  );
}

const NotFoundPage = lazy(() => import('@/pages/NotFound'));

// Known application path prefixes — used to distinguish unknown (404) paths
// from valid protected paths when the user is unauthenticated.
const KNOWN_APP_PATHS: string[] = [
  '/', '/dashboard', '/e-consent', '/application', '/buy-a-home', '/refinance',
  '/pre-approval', '/my-loan', '/my-home', '/tasks', '/documents', '/conditions', '/payments',
  '/income', '/contacts', '/calculator', '/calculators', '/review-application',
  '/settings', '/notifications', '/sophia', '/service-health', '/design-system', '/profile',
];

function isKnownAppPath(pathname: string): boolean {
  return KNOWN_APP_PATHS.some(
    (p) => pathname === p || pathname.startsWith(p === '/' ? p : p + '/')
  );
}

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Show loading spinner while auth state is being determined
  // P0 WHITE-PAGE FIX: use LoadingWithTimeout so the spinner eventually shows a
  // visible "Retry" message if loading hangs (API cold start, MSAL hang, etc.)
  if (isLoading) {
    return <LoadingWithTimeout />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // BUG-DEEP-010 fix: if the path doesn't match any known app route, show the
    // 404 NotFound page directly instead of redirecting to login. Without this,
    // unauthenticated users hitting /totally-nonexistent-page-xyz123 land on the
    // login page instead of a proper 404 — confusing UX and makes 404s undetectable.
    if (!isKnownAppPath(location.pathname)) {
      return (
        <Suspense fallback={null}>
          <NotFoundPage />
        </Suspense>
      );
    }

    // If user object exists with an id but auth failed, session was cleared/expired
    const sessionExpired = Boolean(user?.id);
    // Encode returnUrl as query param so it survives page refreshes (not just React Router state)
    const returnPath = location.pathname + (location.search || '');
    const loginParams = new URLSearchParams();
    if (returnPath && returnPath !== '/') {
      loginParams.set('returnUrl', returnPath);
    }
    if (sessionExpired) {
      loginParams.set('sessionExpired', 'true');
    }
    const loginSearch = loginParams.toString();
    return (
      <Navigate 
        to={`/login${loginSearch ? `?${loginSearch}` : ''}`}
        state={{ from: location, ...(sessionExpired && { sessionExpired: true }) }} 
        replace 
        data-testid="auth-redirect"
      />
    );
  }

  // User is authenticated, render protected content
  return <>{children}</>;
}
